import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './LoginPage.css';

export function LoginPage() {
    const { signIn, signInWithGoogle, signInWithAzure, signInWithFacebook, user } = useAuth();
    const history = useHistory();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [azureLoading, setAzureLoading] = useState(false);
    const [facebookLoading, setFacebookLoading] = useState(false);

    useEffect(() => {
        if (!user) return;
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get('returnUrl') || '/select-meeting';
        // Only allow same-origin relative redirects
        history.replace(returnUrl.startsWith('/') ? returnUrl : '/select-meeting');
    }, [user, history]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const oauthError = params.get('oauthError');
        const passwordReset = params.get('passwordReset');
        if (oauthError) {
            setError(decodeURIComponent(oauthError));
        }
        if (passwordReset === 'success') {
            setInfo('Your password has been reset. Sign in with your new password.');
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const err = await signIn(email.trim(), password);
        if (err) {
            setError(err);
            setLoading(false);
        }
        // On success onAuthStateChange fires → user populates → useEffect above redirects
    };

    const handleGoogle = async () => {
        setGoogleLoading(true);
        setError('');
        const { error } = await signInWithGoogle();
        if (error) {
            setError(error.message);
            setGoogleLoading(false);
        }
        // On success Supabase redirects to Google — browser navigates away
    };

    const handleAzure = async () => {
        setAzureLoading(true);
        setError('');
        const { error } = await signInWithAzure();
        if (error) {
            setError(error.message);
            setAzureLoading(false);
        }
        // On success Supabase redirects to Microsoft — browser navigates away
    };

    const handleFacebook = async () => {
        setFacebookLoading(true);
        setError('');
        const { error } = await signInWithFacebook();
        if (error) {
            setError(error.message);
            setFacebookLoading(false);
        }
        // On success Supabase redirects to Facebook — browser navigates away
    };

    const providersLoading = googleLoading || azureLoading || facebookLoading || loading;

    return (
        <section className="login-page-wrap">
            <div className="login-card">
                <div className="login-brand-row">
                    <div className="login-brand-mark" aria-hidden="true">R</div>
                    <h1 className="login-brand-title">regattamanager</h1>
                </div>

                <p className="login-subtitle">Sign in to manage your regatta</p>

                {error && <div className="alert alert-danger login-alert">{error}</div>}
                {info && <div className="alert alert-success login-alert">{info}</div>}

                <div className="login-actions" role="group" aria-label="Sign in methods">
                    <button
                        type="button"
                        className="btn btn-outline-dark login-provider-btn d-flex align-items-center justify-content-center gap-2"
                        onClick={handleGoogle}
                        disabled={providersLoading}
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.8 2.5 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.8 6C12.4 13 17.8 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.5-4.1 7.1-10.2 7.1-17.1z"/>
                            <path fill="#FBBC05" d="M10.5 28.7A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.8.9 7.4 2.5 10.6l8-5.9z"/>
                            <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.4l-7.6-5.9c-2 1.4-4.6 2.2-7.4 2.2-6.2 0-11.5-4.2-13.5-9.9l-8 5.9C6.5 42.6 14.6 48 24 48z"/>
                        </svg>
                        <span>{googleLoading ? 'Redirecting...' : 'Sign in with Google'}</span>
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-secondary login-provider-btn d-flex align-items-center justify-content-center gap-2"
                        onClick={handleAzure}
                        disabled={providersLoading}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="currentColor" d="M11.4 0H0v11.4h11.4V0zM24 0H12.6v11.4H24V0zM11.4 12.6H0V24h11.4V12.6zM24 12.6H12.6V24H24V12.6z"/>
                        </svg>
                        <span>{azureLoading ? 'Redirecting...' : 'Sign in with Microsoft'}</span>
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-primary login-provider-btn d-flex align-items-center justify-content-center gap-2"
                        onClick={handleFacebook}
                        disabled={providersLoading}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="currentColor" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.099 4.388 23.094 10.125 24v-8.438H7.078v-3.489h3.047V9.413c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.237 2.686.237v2.953h-1.514c-1.491 0-1.956.93-1.956 1.885v2.254h3.328l-.532 3.489h-2.796V24C19.612 23.094 24 18.099 24 12.073z"/>
                        </svg>
                        <span>{facebookLoading ? 'Redirecting...' : 'Sign in with Facebook'}</span>
                    </button>
                </div>

                <div className="login-divider" aria-hidden="true">
                    <span>Or</span>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="mb-3">
                        <label className="form-label" htmlFor="login-email">Email</label>
                        <input
                            id="login-email"
                            type="email"
                            className="form-control"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <div className="login-label-row">
                            <label className="form-label" htmlFor="login-password">Password</label>
                            <Link className="login-inline-link" to="/forgot-password">Forgot password?</Link>
                        </div>
                        <input
                            id="login-password"
                            type="password"
                            className="form-control"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                    </div>
                    <button type="submit" className="btn btn-primary w-100 login-submit-btn" disabled={providersLoading}>
                        {loading ? 'Signing in...' : 'Continue'}
                    </button>
                </form>

                <p className="login-footer-note">
                    Don&apos;t have an account? <Link to="/signup">Sign up for free</Link>
                </p>
            </div>
        </section>
    );
}
